import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { JobApplication } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime as fmt } from '../utils/date';
import { DATA_DICTIONARY_TYPES, useDataDictionaries } from '../hooks/useDataDictionary';
import { getDictDisplayValue } from '../utils/i18n';

const replyIcon = (replied: boolean) =>
  replied
    ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
    : <XCircle className="h-4 w-4 text-muted-foreground" />;

const MyApplicationsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const { dictionariesByParent } = useDataDictionaries({ parentCodes: [DATA_DICTIONARY_TYPES.JOB_TITLE] });
  const jobTitleDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.JOB_TITLE) || [];
  const getJobTitle = (code: string) => {
    const dict = jobTitleDicts.find(d => d.code === code);
    return dict ? getDictDisplayValue(dict) : code;
  };

  useEffect(() => {
    if (authLoading || !user?.id) return;
    apiService.jobApplications.getByHelper(user.id!).then((res: any) => {
      if (res?.success && res.data) setApplications(res.data);
    }).finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) return (
    <div className="flex min-h-[calc(100vh-64px)] items-center justify-center">
      <Loading message={t('common.loading', '載入中...')} />
    </div>
  );

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />{t('common.back', '返回')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('myApplications.title', '申請狀態')}（{applications.length}）</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {applications.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
              {t('myApplications.noRecords', '暫無申請記錄。')}
            </div>
          ) : (
            applications.map((app) => {
              const replied = app.status !== 'PENDING';
              const hasReplyContent = app.status === 'ACCEPTED' || app.status === 'REJECTED';
              return (
                <div key={app.id} className="rounded-2xl border border-border/70 bg-background px-4 py-4 space-y-3">
                  <p className="font-medium text-foreground leading-snug">{getJobTitle(app.jobPostingTitle)}</p>
                  <div className="flex flex-col gap-1.5 text-sm">
                    <div className="flex items-start gap-2 text-muted-foreground">
                      {replyIcon(replied)}
                      <span className="break-all">{t('myApplications.replied', '已回覆')}：{replied ? t('common.yes', '是') : t('common.no', '否')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      {hasReplyContent ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />}
                      <span>{t('myApplications.replyContent', '回覆內容')}：{hasReplyContent ? (app.status === 'ACCEPTED' ? t('myApplications.accepted', '接受申請') : t('myApplications.rejected', '拒絕申請')) : '—'}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{t('myApplications.appliedDate', '發出日期')}：{fmt(app.appliedDate || app.createdAt)}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{t('myApplications.lastUpdated', '最後更新')}：{fmt(app.updatedAt)}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="px-0 text-primary hover:text-primary/80" onClick={() => navigate(`/job-postings/${app.jobPostingId}/detail`)}>
                    {t('myApplications.viewJob', '查看職位')}
                  </Button>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyApplicationsPage;
